/**
 * Mancala engine (Kalah rules). Pure and side-effect free, like the other kinds: the
 * `game:<id>` doc holds only an append-only move log, and `deriveMancala` replays it.
 *
 * Board: 14 slots, sown counter-clockwise.
 *   0..5   player 0's pits (left to right from player 0's seat)
 *   6      player 0's store
 *   7..12  player 1's pits
 *   13     player 1's store
 *
 * Rules (the common Kalah set most apps ship):
 *  - Pick one of your non-empty pits; sow its seeds one per slot, skipping the opponent's store.
 *  - Last seed in your own store: you go again.
 *  - Last seed in one of your own empty pits, with seeds across from it: you capture both.
 *  - When either row is empty after a move, each player banks what's left on their side.
 *    Most seeds in the store wins; equal is a draw.
 */
export const MANCALA_PITS = 6;
export const MANCALA_SEEDS = 4;
export const MANCALA_SLOTS = 14;
export function mancalaStore(player) {
    return player === 0 ? 6 : 13;
}
/** Absolute slot of a player's pit `pit` (0..5). */
export function mancalaPitSlot(player, pit) {
    return player === 0 ? pit : 7 + pit;
}
export function mancalaOwner(slot) {
    return slot <= 6 ? 0 : 1;
}
export function isMancalaStore(slot) {
    return slot === 6 || slot === 13;
}
export function mancalaOpposite(slot) {
    return 12 - slot;
}
export function initialMancalaBoard(seeds = MANCALA_SEEDS) {
    return Array.from({ length: MANCALA_SLOTS }, (_, i) => (isMancalaStore(i) ? 0 : seeds));
}
function mancalaRowTotal(board, player) {
    let total = 0;
    for (let p = 0; p < MANCALA_PITS; p += 1)
        total += board[mancalaPitSlot(player, p)] ?? 0;
    return total;
}
/** Pits (0..5) `player` may sow from right now. */
export function mancalaLegalPits(board, player) {
    const pits = [];
    for (let p = 0; p < MANCALA_PITS; p += 1)
        if ((board[mancalaPitSlot(player, p)] ?? 0) > 0)
            pits.push(p);
    return pits;
}
/**
 * Plays one sowing. Returns `null` for an illegal pit (out of range or empty). Used both
 * by `deriveMancala` and by the board's "where will this land?" preview.
 */
export function sowMancala(input, player, pit) {
    if (!Number.isInteger(pit) || pit < 0 || pit >= MANCALA_PITS)
        return null;
    const board = input.slice();
    const from = mancalaPitSlot(player, pit);
    let seeds = board[from] ?? 0;
    if (seeds <= 0)
        return null;
    const steps = [];
    board[from] = 0;
    steps.push(board.slice());
    const skip = mancalaStore(player === 0 ? 1 : 0);
    let slot = from;
    while (seeds > 0) {
        slot = (slot + 1) % MANCALA_SLOTS;
        if (slot === skip)
            continue;
        board[slot] = (board[slot] ?? 0) + 1;
        seeds -= 1;
        steps.push(board.slice());
    }
    const myStore = mancalaStore(player);
    const extraTurn = slot === myStore;
    let captured = 0;
    let capturedFrom = null;
    if (!isMancalaStore(slot) && mancalaOwner(slot) === player && board[slot] === 1) {
        const opposite = mancalaOpposite(slot);
        const across = board[opposite] ?? 0;
        if (across > 0) {
            captured = across + 1;
            capturedFrom = opposite;
            board[opposite] = 0;
            board[slot] = 0;
            board[myStore] = (board[myStore] ?? 0) + captured;
            steps.push(board.slice());
        }
    }
    let ended = false;
    if (mancalaRowTotal(board, 0) === 0 || mancalaRowTotal(board, 1) === 0) {
        ended = true;
        for (const side of [0, 1]) {
            const store = mancalaStore(side);
            for (let p = 0; p < MANCALA_PITS; p += 1) {
                const i = mancalaPitSlot(side, p);
                board[store] = (board[store] ?? 0) + (board[i] ?? 0);
                board[i] = 0;
            }
        }
        steps.push(board.slice());
    }
    return { board, steps, from, landing: slot, extraTurn, captured, capturedFrom, ended };
}
/**
 * Replays the move log in merged order. Out-of-turn, empty-pit, unknown-player or
 * after-the-end moves are skipped, not errors (same CRDT story as `connect4.ts`).
 */
export function deriveMancala(moves, playerIds) {
    let board = initialMancalaBoard();
    let turn = 0;
    let winner = null;
    let isDraw = false;
    let applied = 0;
    let lastMove = null;
    let lastSharedPhone = false;
    const rejectedMoveIndexes = [];
    for (let i = 0; i < moves.length; i += 1) {
        const move = moves[i];
        if (!move || turn === null || move.playerId !== playerIds[turn]) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        const result = sowMancala(board, turn, move.pit);
        if (!result) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        board = result.board;
        applied += 1;
        lastSharedPhone = move.sharedPhone === true;
        lastMove = {
            player: turn,
            from: result.from,
            landing: result.landing,
            extraTurn: result.extraTurn,
            captured: result.captured,
            capturedFrom: result.capturedFrom,
        };
        if (result.ended) {
            const a = board[6] ?? 0;
            const b = board[13] ?? 0;
            if (a === b)
                isDraw = true;
            else
                winner = a > b ? 0 : 1;
            turn = null;
        }
        else if (!result.extraTurn) {
            turn = turn === 0 ? 1 : 0;
        }
    }
    return {
        board,
        appliedMoves: applied,
        rejectedMoveIndexes,
        turn,
        winner,
        isDraw,
        lastMove,
        lastSharedPhone,
    };
}
//# sourceMappingURL=mancala.js.map